/**
 * Browser and wallet capabilities detection
 */
import { hasSponsoredCalls as checkWalletSponsored } from '../engine/utils/walletCaps';

// Cache for capabilities to avoid repeated checks
const capabilitiesCache = {
  hasWebGL: null,
  hasWebGPU: null,
  hasWebAudio: null,
  hasLocalStorage: null,
  hasIndexedDB: null,
  hasServiceWorker: null,
  hasWebRTC: null,
  hasMiniKit: null,
  hasEthereum: null,
  hasWalletConnect: null,
};

/**
 * Detect browser and wallet capabilities
 * @returns {Object} Object containing detected capabilities
 */
export function detectCapabilities() {
  const capabilities = { ...capabilitiesCache };

  // Only run in browser environment
  if (typeof window === 'undefined') {
    return capabilities;
  }

  try {
    // WebGL support
    capabilities.hasWebGL = !!window.WebGLRenderingContext;
    
    // WebGPU support (experimental)
    capabilities.hasWebGPU = !!(window.GPU || window.GPUDevice);
    
    // Web Audio API support
    capabilities.hasWebAudio = !!(window.AudioContext || window.webkitAudioContext);
    
    // LocalStorage support
    try {
      const storage = window.localStorage;
      const testKey = '__test__';
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      capabilities.hasLocalStorage = true;
    } catch (e) {
      capabilities.hasLocalStorage = false;
    }
    
    // IndexedDB support
    capabilities.hasIndexedDB = !!window.indexedDB;
    
    // Service Worker support
    capabilities.hasServiceWorker = 'serviceWorker' in navigator;
    
    // WebRTC support
    capabilities.hasWebRTC = !!(window.RTCPeerConnection || window.webkitRTCPeerConnection);
    
    // MiniKit support (Farcaster)
    capabilities.hasMiniKit = !!(window.farcaster && window.farcaster.getChannel);
    
    // Ethereum provider support
    capabilities.hasEthereum = !!window.ethereum;
    
    // WalletConnect support
    capabilities.hasWalletConnect = !!(window.WalletConnect || window.WalletConnectModal);
    
    // Update cache
    Object.assign(capabilitiesCache, capabilities);
    
    console.log('[Capabilities] Detected:', capabilities);
  } catch (error) {
    console.error('[Capabilities] Error detecting capabilities:', error);
  }
  
  return capabilities;
}

/**
 * Check if the browser has sponsored calls capability
 * @returns {boolean} Whether sponsored calls are supported
 */
export function hasSponsoredCalls() {
  return checkWalletSponsored();
}

/**
 * Export all capabilities as a single object
 * @returns {Object} All detected capabilities
 */
export function getAllCapabilities() {
  // Ensure capabilities are detected
  detectCapabilities();
  return { ...capabilitiesCache, hasSponsoredCalls: hasSponsoredCalls() };
}
